import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Module-level cache to deduplicate has_role RPC calls across components
let cachedUserId: string | null = null;
let cachedResult: boolean | null = null;
let pendingPromise: Promise<boolean> | null = null;

function checkAdmin(userId: string): Promise<boolean> {
  // Return cached result if same user
  if (cachedUserId === userId && cachedResult !== null) {
    return Promise.resolve(cachedResult);
  }

  // Deduplicate in-flight requests
  if (cachedUserId === userId && pendingPromise) {
    return pendingPromise;
  }

  cachedUserId = userId;
  pendingPromise = Promise.resolve(
    supabase.rpc("has_role", { _user_id: userId, _role: "admin" as const })
  )
    .then(({ data, error }) => {
      const result = !error && data === true;
      cachedResult = result;
      pendingPromise = null;
      return result;
    })
    .catch(() => {
      cachedResult = false;
      pendingPromise = null;
      return false;
    });

  return pendingPromise;
}

// Reset cache on auth change (logout/login)
function resetCache() {
  cachedUserId = null;
  cachedResult = null;
  pendingPromise = null;
}

export function useIsAdmin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      resetCache();
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // If user changed, reset cache
    if (cachedUserId && cachedUserId !== user.id) {
      resetCache();
    }

    checkAdmin(user.id).then((result) => {
      setIsAdmin(result);
      setLoading(false);
    });
  }, [user]);

  return { isAdmin, loading };
}
